import React from "react";

import withStyles from '@mui/styles/withStyles';
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import { capitalizeFirstLetterAllWorlds } from "../../../util/capitalizeFirstLetter";
import { BookingStatus } from "../../../util/enums";

type Props = {
  value?: string;
  backgroundColor?: string;
  dataCy?: string;
  textColor?: string;
  classes?: any;
  className?: string;
};

const styles = () =>
  ({
    customBadge: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: (props: { textColor?: string; backgroundColor?: string }) =>
        props?.backgroundColor,
      padding: "8px 12px",
      height: "32px",
      borderRadius: "100px",
      whiteSpace: "nowrap",
      width: "fit-content",
    },
    textValue: {
      color: (props: { textColor?: string }) => props?.textColor,
      fontFamily: "Manrope",
      fontSize: "12px",
      fontWeight: 600,
      lineHeight: "16px",
    },
  } as const);

const SimpleBadge = (props: Props) => {
  const { classes, value, className, dataCy } = props;

  return (
    <div className={classnames(classes.customBadge, className)}>
      <span className={classes.textValue} data-cy={dataCy}>
        {value}
      </span>
    </div>
  );
};

const StyledBadge = withStyles(styles)(SimpleBadge);

export const BadgeBookingStatus = (props: Props) => {
  const { value, className, dataCy } = props;
  const { t: translate } = useTranslation();

  const renderBadge = (
    backgroundColor: string,
    textColor: string,
    value?: string,
    formatWord = true
  ) => {
    if (value) {
      const formatted = formatWord ? capitalizeFirstLetterAllWorlds(value) : value;

      return (
        <StyledBadge
          dataCy={dataCy}
          backgroundColor={backgroundColor}
          textColor={textColor}
          value={formatted}
          className={className}
        />
      );
    }

    return <> </>;
  };

  switch (value?.toLowerCase()) {
    case BookingStatus.REJECTED:
    case BookingStatus.CANCELLED:
      return renderBadge("#FEE4E2", "#F04438", value);
    case BookingStatus.CFS_RECEIVED:
      return renderBadge("#F4F3FF", "#6938EF", translate("receivedByCFS").toString(), false);
    case BookingStatus.CFS_REVIEW:
      return renderBadge("#F4F3FF", "#6938EF", translate("cfsReview").toString(), false);
    case BookingStatus.UNDER_REVIEW:
    case BookingStatus.CONFIRMED:
      return renderBadge("#F4F3FF", "#6938EF", value);
    case BookingStatus.PENDING:
      return renderBadge("#FFFAEB", "#DC6803", value);
    case BookingStatus.DELIVERED:
      return renderBadge("#FCE7F6", "#DD2590", value);
    case BookingStatus.SUBMITTED:
      return renderBadge("#EAECF5", "#344054", value);
    case BookingStatus.PARTIALLY_DELIVERED:
      return renderBadge("#FEF0C7", "#DC6803", value);
    case BookingStatus.RECEIVED:
      return renderBadge("#ECFDF3", "#039855", value);
    default:
      return renderBadge("#e6fffd", "#00D0BD", value);
  }
};

export default BadgeBookingStatus;
